import { useState, useEffect, useCallback } from "react";
import { collection, query, where, orderBy, onSnapshot, getDocs } from "firebase/firestore";
import { db } from "../../firebase/config";
import type { Task, TaskResult } from "../lib/taskEngine";

interface TeacherTasksState {
  tasks: Task[];
  results: Record<string, TaskResult[]>;
  isLoading: boolean;
  error: string | null;
}

export function useTeacherTasks(teacherId: string) {
  const [state, setState] = useState<TeacherTasksState>({
    tasks: [],
    results: {},
    isLoading: true,
    error: null,
  });

  // Escuchar tareas del profesor en tiempo real
  useEffect(() => {
    if (!teacherId) {
      setState((s) => ({ ...s, isLoading: false }));
      return;
    }

    setState((s) => ({ ...s, isLoading: true, error: null }));

    const q = query(
      collection(db, "bordona_tasks"),
      where("teacherId", "==", teacherId),
      orderBy("createdAt", "desc")
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const tasks = snap.docs.map((d) => ({ id: d.id, ...d.data() } as Task));
        setState((s) => ({ ...s, tasks, isLoading: false }));
      },
      (err) => {
        setState((s) => ({
          ...s,
          isLoading: false,
          error: err instanceof Error ? err.message : "Error al cargar tareas",
        }));
      }
    );

    return () => unsub();
  }, [teacherId]);

  // Cargar resultados de una tarea específica
  const loadResults = useCallback(async (taskId: string) => {
    if (!teacherId || !taskId) return [];
    try {
      const resultsSnap = await getDocs(
        query(
          collection(db, "bordona_results"),
          where("taskId", "==", taskId),
          where("teacherId", "==", teacherId)
        )
      );
      const results = resultsSnap.docs.map((d) => d.data() as TaskResult);
      setState((s) => ({
        ...s,
        results: { ...s.results, [taskId]: results },
      }));
      return results;
    } catch (err) {
      console.warn("No se pudieron cargar resultados de la tarea:", err);
      return [];
    }
  }, [teacherId]);

  return { ...state, loadResults };
}
